import type { UntitledIconData } from '@faceup/icons'
import type { RouteCallback } from '@faceup/router'
import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { AdminMenuPanel } from './AdminMenuPanel'
import { AdminMenuRail } from './AdminMenuRail'
import { isRailItemVisible } from './adminMenuUtils'
import { findSectionKey, useActiveMenuKey } from './useActiveMenuKey'
import { useAdminMenuExpansion } from './useAdminMenuExpansion'

type AdminMenuEntry = {
  key: string
  label: string
  icon: UntitledIconData
  visible?: boolean
  badgeValue?: number
  isNew?: boolean
}

type AdminMenuLink = {
  link: RouteCallback
  match?: (RouteCallback | string)[]
}

export type AdminMenuItem = AdminMenuEntry &
  (
    | (AdminMenuLink & { href?: never; onClick?: never; secondary?: never })
    | { href: string; onClick?: () => void; link?: never; match?: never; secondary?: never }
    | { onClick: () => void; href?: never; link?: never; match?: never; secondary?: never }
    | {
        secondary: AdminMenuSecondary
        link?: never
        match?: never
        href?: never
        onClick?: never
      }
  )

export type AdminMenuSecondary = {
  sections: AdminMenuSection[]
  footerSections?: AdminMenuSection[]
}

export type AdminMenuSection = {
  key: string
  title?: string
  visible?: boolean
  items: AdminMenuSubItem[]
}

export type AdminMenuSubItem = AdminMenuEntry &
  (
    | (AdminMenuLink & { onClick?: never; active?: never })
    | { onClick: () => void; active?: boolean; link?: never; match?: never }
  )

export type AdminMenuLogo = {
  src: string
  name: string
  link: RouteCallback
  backgroundColor?: string
  isFullBleed?: boolean
  badge?: string
  caption?: string
}

type AdminMenuProps = {
  label: string
  logo: AdminMenuLogo
  items: AdminMenuItem[]
  footerItems?: AdminMenuItem[]
  activeKey?: string
}

const usePanelScrollOffsets = (sectionKey: string | undefined) => {
  const [offsets] = useState(() => new Map<string, number>())

  useEffect(() => {
    const staleKeys = [...offsets.keys()].filter(key => key !== sectionKey)
    staleKeys.forEach(key => offsets.delete(key))
  }, [offsets, sectionKey])

  return offsets
}

export const AdminMenu = ({
  label,
  logo,
  items,
  footerItems = [],
  activeKey: activeKeyOverride,
}: AdminMenuProps) => {
  const allItems = [...items, ...footerItems]
  const derived = useActiveMenuKey(allItems)

  const activeKey = activeKeyOverride ?? derived.activeKey
  const sectionKey = activeKeyOverride
    ? findSectionKey(allItems, activeKeyOverride)
    : derived.sectionKey

  const scrollOffsets = usePanelScrollOffsets(sectionKey)

  const visibleItems = allItems.filter(isRailItemVisible)
  const section = visibleItems.find(item => item.key === sectionKey)

  const {
    isExpanded,
    isEngaged,
    pushedPanel,
    isPushedPanelIn,
    isLanding,
    highlightedKey,
    ...expansion
  } = useAdminMenuExpansion({
    sectionKey,
    hasPanel: key => Boolean(visibleItems.find(item => item.key === key)?.secondary),
  })

  const pushedSection = visibleItems.find(item => item.key === pushedPanel?.key)
  const pushedActiveKey = pushedSection?.key === sectionKey ? activeKey : undefined

  return (
    <nav
      aria-label={label}
      className={twMerge(
        'relative z-30 shrink-0 transition-[width] duration-[340ms] ease-nav motion-reduce:transition-none print:hidden',
        section ? 'w-83' : 'w-16'
      )}
      {...expansion.navProps}
    >
      <AdminMenuRail
        logo={logo}
        items={items}
        footerItems={footerItems}
        isExpanded={isExpanded}
        hoverProps={expansion.railProps}
        onItemHover={expansion.onItemHover}
        onSelect={expansion.onSelect}
        activeKey={sectionKey ?? activeKey}
        highlightedKey={highlightedKey}
      />
      <div
        className={twMerge(
          'absolute inset-y-2 z-20 w-2 bg-(--admin-canvas) transition-[inset-inline-start] duration-[340ms] ease-nav motion-reduce:transition-none',
          isExpanded ? 'start-65' : 'start-16'
        )}
      />
      {!isEngaged && section?.secondary && (
        <AdminMenuPanel
          sectionKey={section.key}
          title={section.label}
          secondary={section.secondary}
          activeKey={activeKey}
          onSelect={expansion.onSelect}
          scrollOffsets={scrollOffsets}
        />
      )}
      {pushedSection?.secondary && pushedPanel && (
        <AdminMenuPanel
          sectionKey={pushedSection.key}
          title={pushedSection.label}
          secondary={pushedSection.secondary}
          activeKey={pushedActiveKey}
          onSelect={expansion.onSelect}
          scrollOffsets={scrollOffsets}
          hoverProps={expansion.pushedPanelProps}
          push={{
            entrance: pushedPanel.entrance,
            isLanding,
            isExiting: !isPushedPanelIn,
          }}
        />
      )}
    </nav>
  )
}
